import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import NotificationRowCtrl from '../notification-row';

import styled from 'styled-components';

import Flex from '@components/layout/flex';
import AcceptIcon from '@components/basics/icons/accept';
import DeleteIcon from '@components/basics/icons/delete';

import SnackbarStatus from '@components/basics/snackbar/snackbar-status';
import { Translate } from 'react-localize-redux';
import { SnackbarContainer } from '@components/basics/snackbar/snackbar-container';

import {
  AcceptInvite,
  RejectInvite,
} from '@webapp/store/invites/action-creators';
import { TracInvite } from '@services/invites/types/trac-invite';
import { ACCEPT_INVITE, REJECT_INVITE } from '@webapp/store/invites/actions';
import AppState from '@webapp/store/state';

import TextWrapper from '../text-wrapper';

export interface NotificationRequestCtrlProps {
  className?: string;
  children: React.ReactNode;
  isRead: boolean;
  isAccepted?: boolean;
  invite?: TracInvite;
  imgSrc: string;
  imgAlt?: string;
  timestamp: string;
  isLoading: boolean;
  id: string;
  acceptInvite: (invite: TracInvite) => AcceptInvite;
  rejectInvite: (invite: TracInvite) => RejectInvite;
  clearNotification: (id: string) => void;
  notificationClick?: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  acceptInvite: (invite: TracInvite) => dispatch(new AcceptInvite(invite)),
  rejectInvite: (invite: TracInvite) => dispatch(new RejectInvite(invite)),
});

const mapStateToProps = (state: AppState) => ({
  isLoading: !!state.ui.loading[ACCEPT_INVITE || REJECT_INVITE],
});

const NotificationRequestCtrl: React.FC<NotificationRequestCtrlProps> = ({
  className,
  children,
  isRead,
  invite,
  imgSrc,
  imgAlt,
  timestamp,
  acceptInvite,
  rejectInvite,
  id,
  clearNotification,
  notificationClick,
}) => {
  const [showNotification, setShowNotification] = React.useState(false);
  const [snackBar, setSnackBar] = React.useState('');
  return (
    <>
      <NotificationRowCtrl
        isRead={isRead}
        imgSrc={imgSrc}
        imgAlt={imgAlt}
        timestamp={timestamp}
        className={className}
        id={id}
        clearNotification={clearNotification}
        notificationClick={notificationClick}
        actions={
          invite && invite.type === 'team' ? (
            invite.status === 'pending' ? (
              <NotificationChoice>
                <StyledDeleteIcon
                  onClick={() => {
                    rejectInvite(invite);
                    setShowNotification(true);
                    setSnackBar('INVITE.REJECTED');
                  }}
                />
                <StyledAcceptIcon
                  onClick={() => {
                    acceptInvite(invite);
                    setShowNotification(true);
                    setSnackBar('INVITE.ACCEPTED');
                  }}
                />
              </NotificationChoice>
            ) : (
              <NotificationChoice>
                {invite.status === 'accepted' ? (
                  <StyledAcceptIcon noHover />
                ) : (
                  <StyledDeleteIcon noHover className="no-margin" />
                )}
              </NotificationChoice>
            )
          ) : null
        }
      >
        <TextWrapper
          justifyContent="flex-start"
          alignItems="baseline"
          isRead={isRead}
        >
          {children}
        </TextWrapper>
      </NotificationRowCtrl>
      {showNotification ? (
        <SnackbarContainer>
          <SnackbarStatus
            status={snackBar === 'INVITE.ACCEPTED' ? 'success' : 'error'}
            title={<Translate id={snackBar} />}
            onClose={() => setShowNotification(false)}
          />
        </SnackbarContainer>
      ) : null}
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NotificationRequestCtrl);

const NotificationChoice = styled(Flex)`
  @media ${({ theme }) => theme.device.laptop} {
    margin: 0 24px;
  }
`;

const StyledAcceptIcon = styled(AcceptIcon)`
  width: 40px;
  height: 40px;
  @media ${({ theme }) => theme.device.laptop} {
    width: 32px;
    height: 32px;
  }
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  margin-right: 14px;
  width: 40px;
  height: 40px;
  &.no-margin {
    margin-right: 0;
  }
  @media ${({ theme }) => theme.device.laptop} {
    width: 32px;
    height: 32px;
  }
`;
